import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2, Clock, DollarSign, FileText, Upload, Check,
  AlertCircle, ChevronDown, ChevronUp, Calculator,
  Paperclip, Download, Send, ArrowLeft, Info, Star,
  Package, Wrench, Home, Zap, Calendar, CheckCircle,
  X, Loader2, Mail, Phone, Plus
} from 'lucide-react';
import toast from 'react-hot-toast';

// Types
interface LineItem {
  id: string;
  category: string;
  name: string;
  description?: string;
  typical_unit: string;
  estimated_duration_days?: number;
  trade_type: string;
  quantity?: number;
  unit?: string;
  estimated_cost?: number;
}

interface ProjectDetails {
  id: string;
  project_name: string;
  address: string;
  description: string;
  square_footage?: number;
  project_type: string;
  deadline?: string;
  scope_of_work: string;
  attachments: string[];
}

interface VendorBid {
  line_item_id: string;
  can_perform: boolean;
  bid_amount: number;
  timeline_days: number;
  materials_cost: number;
  labor_cost: number;
  vendor_notes: string;
  confidence_level: number;
}

interface UploadedFile {
  name: string;
  size: number;
  type: string;
  url?: string;
}

const VendorBiddingPage: React.FC = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Debug logging
  console.log('🏗️ VendorBiddingPage loaded with projectId:', projectId);
  console.log('🔗 Current URL:', window.location.href);
  
  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bidSubmitted, setBidSubmitted] = useState(false);
  const [project, setProject] = useState<ProjectDetails | null>(null);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [vendorBids, setVendorBids] = useState<Record<string, VendorBid>>({});
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [customLineItems, setCustomLineItems] = useState<Record<string, LineItem[]>>({});
  const [vendorInfo, setVendorInfo] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    license_number: '',
    insurance_amount: ''
  });
  const [generalNotes, setGeneralNotes] = useState('');
  const [totalBidAmount, setTotalBidAmount] = useState(0);
  const [roomSelections, setRoomSelections] = useState<any[]>([]);

  // Load project data
  useEffect(() => {
    loadProjectData();
  }, [projectId]);

  // Calculate total when bids change
  useEffect(() => {
    const total = Object.values(vendorBids).reduce((sum, bid) => {
      return bid.can_perform ? sum + (bid.bid_amount || 0) : sum;
    }, 0);
    setTotalBidAmount(total);
  }, [vendorBids]);

  const loadProjectData = async () => {
    try {
      setLoading(true);

      // Load project details, line items, and room selections
      const apiUrl = import.meta.env.VITE_API_URL || 'https://homequest-api-1.onrender.com';
      const [projectResponse, lineItemsResponse, selectionsResponse] = await Promise.all([
        fetch(`${apiUrl}/api/vendor-bidding/projects/${projectId}/bidding-details`),
        fetch(`${apiUrl}/api/vendor-bidding/projects/${projectId}/line-items`),
        fetch(`${apiUrl}/api/selections/project/${projectId}`)
      ]);

      if (!projectResponse.ok || !lineItemsResponse.ok) {
        throw new Error('Failed to load project data');
      }

      const projectData = await projectResponse.json();
      const lineItemsData = await lineItemsResponse.json();

      console.log('📊 Project Data received:', projectData);
      console.log('📦 Line Items received:', lineItemsData);

      setProject(projectData);
      setLineItems(lineItemsData);

      // Load room selections if available
      if (selectionsResponse.ok) {
        const selectionsData = await selectionsResponse.json();
        if (selectionsData.success && selectionsData.selections) {
          setRoomSelections(selectionsData.selections);
          console.log(`✅ Loaded ${selectionsData.selections.length} room selection document(s)`);
        }
      }

      // Initialize expanded categories
      // Categories start collapsed by default - no need to expand them

    } catch (error) {
      console.error('Error loading project data:', error);
      toast.error('Failed to load project information');
    } finally {
      setLoading(false);
    }
  };

  const updateVendorBid = (lineItemId: string, updates: Partial<VendorBid>) => {
    setVendorBids(prev => ({
      ...prev,
      [lineItemId]: {
        line_item_id: lineItemId,
        can_perform: false,
        bid_amount: 0,
        timeline_days: 0,
        materials_cost: 0,
        labor_cost: 0,
        vendor_notes: '',
        confidence_level: 3,
        ...prev[lineItemId],
        ...updates
      }
    }));
  };

  const addCustomLineItem = (category: string) => {
    const newId = `custom-${Date.now()}`;
    const newItem: LineItem = {
      id: newId,
      category: category,
      name: '',
      description: '',
      typical_unit: 'each',
      estimated_duration_days: 1,
      trade_type: 'custom',
      quantity: 1,
      unit: 'each',
      estimated_cost: 0
    };

    setCustomLineItems(prev => ({
      ...prev,
      [category]: [...(prev[category] || []), newItem]
    }));

    // Expand the category to show the new custom item
    setExpandedCategories(prev => ({
      ...prev,
      [category]: true
    }));

    // Automatically mark the custom item as selected
    updateVendorBid(newId, { can_perform: true });
  };

  const removeCustomLineItem = (category: string, itemId: string) => {
    setCustomLineItems(prev => ({
      ...prev,
      [category]: prev[category].filter(item => item.id !== itemId)
    }));

    // Remove the bid for this item
    setVendorBids(prev => {
      const newBids = { ...prev };
      delete newBids[itemId];
      return newBids;
    });
  };

  const updateCustomLineItem = (itemId: string, updates: Partial<LineItem>) => {
    setCustomLineItems(prev => {
      const newCustomItems = { ...prev };
      for (const category in newCustomItems) {
        newCustomItems[category] = newCustomItems[category].map(item =>
          item.id === itemId ? { ...item, ...updates } : item
        );
      }
      return newCustomItems;
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const formData = new FormData();
    const validFiles: File[] = [];

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }
      formData.append('files', file);
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://homequest-api-1.onrender.com';
      const response = await fetch(`${apiUrl}/api/vendor-bidding/vendor/upload-files`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload files');
      }

      const result = await response.json();

      if (result.success && result.files) {
        setUploadedFiles(prev => [...prev, ...result.files]);
        toast.success(`${validFiles.length} file(s) uploaded successfully`);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files. Please try again.');
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const submitBid = async () => {
    try {
      setSubmitting(true);

      // Validate required fields
      if (!vendorInfo.company_name || !vendorInfo.contact_name || !vendorInfo.email) {
        toast.error('Please fill in all required vendor information');
        return;
      }

      // Validate COI is uploaded
      const hasCOI = uploadedFiles.some(file => 
        file.name.toLowerCase().includes('coi') || 
        file.name.toLowerCase().includes('certificate') || 
        file.name.toLowerCase().includes('insurance')
      );
      
      if (!hasCOI) {
        toast.error('Certificate of Insurance (COI) is required. Please upload your COI before submitting.');
        return;
      }

      const selectedBids = Object.values(vendorBids).filter(bid => bid.can_perform);
      if (selectedBids.length === 0) {
        toast.error('Please select at least one line item to bid on');
        return;
      }

      // Submit bid - enrich with line item names and categories
      const enrichedBids = Object.values(vendorBids)
        .filter(bid => bid.can_perform)
        .map(bid => {
          // Find the corresponding line item to get name and category
          const lineItem = [...lineItems, ...Object.values(customLineItems).flat()]
            .find(item => item.id === bid.line_item_id);

          return {
            ...bid,
            line_item_name: lineItem?.name || bid.line_item_id,
            line_item_category: lineItem?.category || ''
          };
        });

      const bidData = {
        project_id: projectId,
        vendor_info: vendorInfo,
        line_item_bids: enrichedBids,
        general_notes: generalNotes,
        total_bid_amount: totalBidAmount,
        uploaded_files: uploadedFiles,
        submitted_at: new Date().toISOString()
      };

      const apiUrl = import.meta.env.VITE_API_URL || 'https://homequest-api-1.onrender.com';
      const response = await fetch(`${apiUrl}/api/vendor-bidding/vendor/submit-bid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bidData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit bid');
      }

      const result = await response.json();
      
      // Show success message with more details
      toast.success('✅ Bid submitted successfully! Confirmation email sent.', {
        duration: 4000,
        style: {
          background: '#10B981',
          color: 'white',
          padding: '16px',
          fontSize: '16px'
        }
      });
      
      // Set bid submitted state to show confirmation page
      setBidSubmitted(true);

    } catch (error) {
      console.error('Error submitting bid:', error);
      toast.error('Failed to submit bid. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleCategorySelection = (category: string, items: LineItem[]) => {
    const allSelected = items.every(item => vendorBids[item.id]?.can_perform);
    
    const newBids = { ...vendorBids };
    items.forEach(item => {
      if (!allSelected) {
        // Select all items in category
        newBids[item.id] = {
          line_item_id: item.id,
          can_perform: true,
          bid_amount: 0,
          timeline_days: 0,
          materials_cost: 0,
          labor_cost: 0,
          confidence_level: 3,
          vendor_notes: ''
        };
      } else {
        // Deselect all items in category
        newBids[item.id] = {
          line_item_id: item.id,
          can_perform: false,
          bid_amount: 0,
          timeline_days: 0,
          materials_cost: 0,
          labor_cost: 0,
          confidence_level: 3,
          vendor_notes: ''
        };
      }
    });
    
    setVendorBids(newBids);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Group all line items by category (no filtering)
  const groupedLineItems = (Array.isArray(lineItems) ? lineItems : []).reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, LineItem[]>);

  // Add custom line items to their respective categories
  Object.entries(customLineItems).forEach(([category, items]) => {
    if (!groupedLineItems[category]) {
      groupedLineItems[category] = [];
    }
    groupedLineItems[category] = [...groupedLineItems[category], ...items];
  });

  const getCategoryIcon = (category: string) => {
    const iconMap: Record<string, React.ElementType> = {
      'Preparation & Preliminaries': FileText,
      'Excavation & Foundation': Building2,
      'Rough Structure': Home,
      'Full Enclosure': Package,
      'Finishing Trades': Wrench,
      'Completion & Inspection': CheckCircle,
      'Cabinet Selections': Package,
      'Countertop Selections': Package,
      'Appliance Selections': Zap,
      'Kitchen Fixture Selections': Wrench
    };
    return iconMap[category] || Package;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Project Not Found</h2>
          <p className="text-gray-600">The project you're looking for doesn't exist or has expired.</p>
        </div>
      </div>
    );
  }

  // Show confirmation page if bid has been submitted
  if (bidSubmitted) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-green-800 mb-4">
              Bid Submitted Successfully!
            </h1>
            
            <div className="space-y-3 text-gray-700 mb-6">
              <p className="flex items-center justify-center">
                <Building2 className="w-5 h-5 text-gray-500 mr-2" />
                Project: {project.project_name}
              </p>
              <p className="flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-gray-500 mr-2" />
                Total Bid: {formatCurrency(totalBidAmount)}
              </p>
              <p className="flex items-center justify-center">
                <Mail className="w-5 h-5 text-gray-500 mr-2" />
                Confirmation sent to: {vendorInfo.email}
              </p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-800 mb-2">What happens next?</h3>
              <ul className="text-sm text-green-700 space-y-1 text-left">
                <li>• You'll receive a confirmation email shortly</li>
                <li>• The project owner will review all bids</li>
                <li>• You'll be contacted if your bid is selected</li>
                <li>• Keep an eye on your email for updates</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/'}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Return to Main Site
              </button>
              
              <button
                onClick={() => setBidSubmitted(false)}
                className="w-full px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
              >
                View Submitted Bid Details
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Building2 className="w-8 h-8 text-blue-500" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Vendor Bidding Portal</h1>
                <p className="text-sm text-gray-500">Submit your bid for this project</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Bid Amount</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totalBidAmount)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Project Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Project Name</label>
                  <p className="text-gray-900">{project.project_name}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Address</label>
                  <p className="text-gray-900">{project.address}</p>
                </div>
                
                {project.square_footage && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Square Footage</label>
                    <p className="text-gray-900">{project.square_footage.toLocaleString()} sq ft</p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Project Type</label>
                  <p className="text-gray-900">{project.project_type}</p>
                </div>
                
                {project.deadline && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bid Deadline</label>
                    <p className="text-gray-900 flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-red-500" />
                      {new Date(project.deadline).toLocaleDateString()}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Description</label>
                  <p className="text-gray-900 text-sm">{project.description}</p>
                </div>
              </div>

              {/* Project Attachments */}
              {project.attachments && project.attachments.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Project Documents</h3>
                  <div className="space-y-2">
                    {project.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center">
                          <Paperclip className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">{attachment}</span>
                        </div>
                        <Download className="w-4 h-4 text-blue-500 cursor-pointer hover:text-blue-600" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Room Selections - Material & Finish Specifications */}
              {roomSelections && roomSelections.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-center mb-3">
                    <Package className="w-5 h-5 text-blue-500 mr-2" />
                    <h3 className="text-sm font-medium text-gray-900">Material & Finish Selections</h3>
                  </div>

                  {/* Project Context */}
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="space-y-1 text-xs">
                      <div>
                        <span className="font-semibold text-gray-700">Project:</span>
                        <span className="text-gray-900 ml-2">{project.project_name}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700">Address:</span>
                        <span className="text-gray-900 ml-2">{project.address}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700">Type:</span>
                        <span className="text-gray-900 ml-2">{project.project_type}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700">Description:</span>
                        <span className="text-gray-900 ml-2">{project.description}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {roomSelections.map((selection: any) => (
                      <div key={selection.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-blue-800 uppercase">{selection.document_name}</span>
                          {selection.validation_status === 'validated' && (
                            <Check className="w-4 h-4 text-green-500" />
                          )}
                        </div>

                        {selection.room_mappings && selection.room_mappings.length > 0 && (
                          <div className="space-y-2">
                            {selection.room_mappings.map((mapping: any, idx: number) => (
                              <div key={idx} className="bg-white rounded p-2 text-xs">
                                <div className="font-semibold text-gray-900 mb-1">{mapping.pdf_room}</div>
                                {mapping.selections && (
                                  <div className="space-y-1 text-gray-700">
                                    {mapping.selections.flooring && (
                                      <div className="flex items-start">
                                        <span className="font-medium min-w-[60px]">Flooring:</span>
                                        <span className="flex-1">
                                          {mapping.selections.flooring.brand} {mapping.selections.flooring.product || ''}
                                          {mapping.selections.flooring.color ? ` - ${mapping.selections.flooring.color}` : ''}
                                        </span>
                                      </div>
                                    )}
                                    {mapping.selections.paint && (
                                      <div className="flex items-start">
                                        <span className="font-medium min-w-[60px]">Paint:</span>
                                        <span className="flex-1">
                                          {mapping.selections.paint.brand} {mapping.selections.paint.color}
                                          {mapping.selections.paint.sku ? ` (${mapping.selections.paint.sku})` : ''}
                                        </span>
                                      </div>
                                    )}
                                    {mapping.selections.cabinetry && (
                                      <div className="flex items-start">
                                        <span className="font-medium min-w-[60px]">Cabinets:</span>
                                        <span className="flex-1">
                                          {mapping.selections.cabinetry.style || mapping.selections.cabinetry.material}
                                          {mapping.selections.cabinetry.color ? ` - ${mapping.selections.cabinetry.color}` : ''}
                                        </span>
                                      </div>
                                    )}
                                    {mapping.selections.countertops && (
                                      <div className="flex items-start">
                                        <span className="font-medium min-w-[60px]">Counters:</span>
                                        <span className="flex-1">
                                          {mapping.selections.countertops.material}
                                          {mapping.selections.countertops.color ? ` - ${mapping.selections.countertops.color}` : ''}
                                        </span>
                                      </div>
                                    )}
                                    {mapping.selections.tile && (
                                      <div className="flex items-start">
                                        <span className="font-medium min-w-[60px]">Tile:</span>
                                        <span className="flex-1">
                                          {mapping.selections.tile.type}
                                          {mapping.selections.tile.size ? ` - ${mapping.selections.tile.size}` : ''}
                                        </span>
                                      </div>
                                    )}
                                    {mapping.selections.fixtures && Array.isArray(mapping.selections.fixtures) && mapping.selections.fixtures.length > 0 && (
                                      <div className="flex items-start">
                                        <span className="font-medium min-w-[60px]">Fixtures:</span>
                                        <span className="flex-1">
                                          {mapping.selections.fixtures.map((f: any, i: number) =>
                                            `${f.type || f.brand}${i < mapping.selections.fixtures.length - 1 ? ', ' : ''}`
                                          )}
                                        </span>
                                      </div>
                                    )}
                                    {mapping.selections.lighting && Array.isArray(mapping.selections.lighting) && mapping.selections.lighting.length > 0 && (
                                      <div className="flex items-start">
                                        <span className="font-medium min-w-[60px]">Lighting:</span>
                                        <span className="flex-1">
                                          {mapping.selections.lighting.map((l: any, i: number) =>
                                            `${l.type || l.brand}${i < mapping.selections.lighting.length - 1 ? ', ' : ''}`
                                          )}
                                        </span>
                                      </div>
                                    )}
                                    {mapping.selections.appliances && Array.isArray(mapping.selections.appliances) && mapping.selections.appliances.length > 0 && (
                                      <div className="flex items-start">
                                        <span className="font-medium min-w-[60px]">Appliances:</span>
                                        <span className="flex-1">
                                          {mapping.selections.appliances.map((a: any, i: number) =>
                                            `${a.brand} ${a.model || ''}${i < mapping.selections.appliances.length - 1 ? ', ' : ''}`
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                    <Info className="w-3 h-3 inline mr-1" />
                    Please bid using these exact specifications unless otherwise approved by the builder.
                  </div>
                </div>
              )}
            </div>

            {/* Vendor Information Form */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Vendor Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={vendorInfo.company_name}
                    onChange={(e) => setVendorInfo(prev => ({ ...prev, company_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your company name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    value={vendorInfo.contact_name}
                    onChange={(e) => setVendorInfo(prev => ({ ...prev, contact_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Primary contact name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={vendorInfo.email}
                    onChange={(e) => setVendorInfo(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="contact@company.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={vendorInfo.phone}
                    onChange={(e) => setVendorInfo(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    License Number
                  </label>
                  <input
                    type="text"
                    value={vendorInfo.license_number}
                    onChange={(e) => setVendorInfo(prev => ({ ...prev, license_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="License #"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Insurance Coverage
                  </label>
                  <input
                    type="text"
                    value={vendorInfo.insurance_amount}
                    onChange={(e) => setVendorInfo(prev => ({ ...prev, insurance_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="$1,000,000"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Line Items Bidding */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Project Scope - Select Items to Bid On</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Check the items you can perform and provide your pricing and timeline
                </p>
              </div>

              <div className="divide-y">
                {Object.entries(groupedLineItems).map(([category, items]) => {
                  const CategoryIcon = getCategoryIcon(category);
                  const isExpanded = expandedCategories[category];
                  const selectedCount = items.filter(item => vendorBids[item.id]?.can_perform).length;
                  
                  return (
                    <div key={category}>
                      {/* Category Header */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleCategory(category)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={items.length > 0 && items.every(item => vendorBids[item.id]?.can_perform)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleCategorySelection(category, items);
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <h3 className="font-medium text-gray-900">{category}</h3>
                            {selectedCount > 0 && (
                              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                {selectedCount} selected
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                addCustomLineItem(category);
                              }}
                              className="p-1 text-green-500 hover:bg-green-50 rounded-full transition-colors"
                              title="Add custom item to this category"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Category Items */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-4 space-y-4">
                              {items.map((item) => {
                                const bid = vendorBids[item.id];
                                const isCustom = item.id.startsWith('custom-');

                                return (
                                  <div key={item.id} className={`border rounded-lg p-4 ${isCustom ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
                                    {/* Item Header */}
                                    <div className="flex items-start space-x-3">
                                      <input
                                        type="checkbox"
                                        checked={bid?.can_perform || false}
                                        onChange={(e) => updateVendorBid(item.id, { can_perform: e.target.checked })}
                                        className="mt-1 w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                      <div className="flex-1">
                                        {isCustom ? (
                                          <div className="space-y-2">
                                            <div className="flex items-center space-x-2">
                                              <span className="px-2 py-1 text-xs font-medium bg-green-600 text-white rounded">
                                                CUSTOM
                                              </span>
                                              <input
                                                type="text"
                                                value={item.name}
                                                onChange={(e) => updateCustomLineItem(item.id, { name: e.target.value })}
                                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Enter item name"
                                              />
                                            </div>
                                            <textarea
                                              value={item.description}
                                              onChange={(e) => updateCustomLineItem(item.id, { description: e.target.value })}
                                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                              placeholder="Enter item description"
                                              rows={2}
                                            />
                                          </div>
                                        ) : (
                                          <>
                                            <h4 className="font-medium text-gray-900">{item.name}</h4>
                                            {item.description && (
                                              <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                                            )}
                                          </>
                                        )}
                                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                          <span>Trade: {item.trade_type}</span>
                                          <span>Unit: {item.typical_unit}</span>
                                          {item.estimated_duration_days && (
                                            <span>Est. {item.estimated_duration_days} days</span>
                                          )}
                                        </div>
                                      </div>
                                      {isCustom && (
                                        <button
                                          onClick={() => removeCustomLineItem(category, item.id)}
                                          className="text-red-500 hover:text-red-700 hover:bg-red-100 rounded p-1"
                                          title="Remove custom item"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>

                                    {/* Bidding Form */}
                                    {bid?.can_perform && (
                                      <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        className="mt-4 pt-4 border-t border-gray-100"
                                      >
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                          <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                              Total Bid Amount
                                            </label>
                                            <div className="relative">
                                              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                              <input
                                                type="number"
                                                value={bid.bid_amount || ''}
                                                onChange={(e) => updateVendorBid(item.id, { bid_amount: parseFloat(e.target.value) || 0 })}
                                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="0.00"
                                              />
                                            </div>
                                          </div>
                                          
                                          <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                              Timeline (Days)
                                            </label>
                                            <div className="relative">
                                              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                              <input
                                                type="number"
                                                value={bid.timeline_days || ''}
                                                onChange={(e) => updateVendorBid(item.id, { timeline_days: parseInt(e.target.value) || 0 })}
                                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="0"
                                              />
                                            </div>
                                          </div>
                                          
                                          <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                              Confidence Level
                                            </label>
                                            <select
                                              value={bid.confidence_level || 3}
                                              onChange={(e) => updateVendorBid(item.id, { confidence_level: parseInt(e.target.value) })}
                                              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                              <option value={1}>1 - Low</option>
                                              <option value={2}>2 - Below Average</option>
                                              <option value={3}>3 - Average</option>
                                              <option value={4}>4 - High</option>
                                              <option value={5}>5 - Very High</option>
                                            </select>
                                          </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                          <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                              Materials Cost
                                            </label>
                                            <div className="relative">
                                              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                              <input
                                                type="number"
                                                value={bid.materials_cost || ''}
                                                onChange={(e) => updateVendorBid(item.id, { materials_cost: parseFloat(e.target.value) || 0 })}
                                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="0.00"
                                              />
                                            </div>
                                          </div>
                                          
                                          <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                              Labor Cost
                                            </label>
                                            <div className="relative">
                                              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                              <input
                                                type="number"
                                                value={bid.labor_cost || ''}
                                                onChange={(e) => updateVendorBid(item.id, { labor_cost: parseFloat(e.target.value) || 0 })}
                                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="0.00"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                        
                                        <div className="mt-4">
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Notes / Special Considerations
                                          </label>
                                          <textarea
                                            value={bid.vendor_notes || ''}
                                            onChange={(e) => updateVendorBid(item.id, { vendor_notes: e.target.value })}
                                            rows={2}
                                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Any additional notes or requirements..."
                                          />
                                        </div>
                                      </motion.div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>

              {/* Certificate of Insurance Section */}
              <div className="p-6 border-t bg-gray-50">
                <div className="flex items-center mb-4">
                  <Upload className="w-6 h-6 text-gray-500 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-800">Certificate of Insurance (COI) - REQUIRED</h3>
                </div>
                
                <p className="text-gray-900 font-medium mb-4">* Required</p>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-white">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-700 mb-2 font-medium">Upload Current Certificate of Insurance</p>
                  <p className="text-sm text-gray-600 mb-4">PDF format required • Must be current and valid</p>
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Upload COI (Required)
                  </button>
                </div>

              </div>

              {/* File Upload Section */}
              <div className="p-6 border-t bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Quote Documents</h3>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Upload your detailed quote, invoice, or supporting documents</p>
                  <p className="text-sm text-gray-500 mb-4">Supports PDF, DOC, XLS, JPG, PNG (Max 10MB each)</p>
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Choose Files
                  </button>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>

                {/* Uploaded Files */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Uploaded Files</h4>
                    <div className="space-y-2">
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                          <div className="flex items-center space-x-3">
                            <Paperclip className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{file.name}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* General Notes */}
              <div className="p-6 border-t">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">General Notes</h3>
                <textarea
                  value={generalNotes}
                  onChange={(e) => setGeneralNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Additional comments, terms, conditions, or questions about the project..."
                />
              </div>

              {/* Submit Section */}
              <div className="p-6 border-t bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-semibold text-gray-900">
                      Total Bid Amount: {formatCurrency(totalBidAmount)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {Object.values(vendorBids).filter(bid => bid.can_perform).length} items selected
                    </p>
                  </div>
                  
                  <button
                    onClick={submitBid}
                    disabled={
                      submitting || 
                      totalBidAmount === 0 ||
                      !vendorInfo.company_name ||
                      !vendorInfo.contact_name ||
                      !vendorInfo.email
                    }
                    className="px-8 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Submit Bid</span>
                      </>
                    )}
                  </button>
                </div>
                
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium mb-1">Before submitting:</p>
                      <ul className="space-y-1 text-xs">
                        <li>• Review all selected items and pricing</li>
                        <li>• Ensure all required vendor information is complete</li>
                        <li>• Upload any supporting documentation</li>
                        <li>• Double-check your total bid amount</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorBiddingPage;